import { fetchData } from '../../../../reusable'

export interface PlacementOption {
  adssettingId: number
  placementName: string
  description: string
  amountPerDay: number
  maxAds: number
  iconName: string
  uz_placementName?: string
  ru_placementName?: string
  uz_description?: string
  ru_description?: string
}

export const placementCropDim: Record<string, { w: number; h: number }> = {
  'Homepage – Top Banner': { w: 312, h: 152 },
  'Homepage – Bottom Banner': { w: 312, h: 79 },
  'Service Category – Top Banner': { w: 312, h: 152 },
  'Service Category – Bottom Banner': { w: 312, h: 79 },
  'Search Results – Top Banner': { w: 312, h: 79 },
  'All Pages – Bottom Banner': { w: 312, h: 79 },
  'All Pages – Top Banner': { w: 312, h: 152 },
}

export const advertisementStatuses = [
    {display: 'DRAFT', api: 'DRAFT'},
    {display: 'UNDER REVIEW', api: 'UNDER_REVIEW'},
    {display: 'APPROVED', api: 'APPROVED'},
    {display: 'ACTIVE', api: 'ACTIVATED'},
    {display: 'REJECTED', api: 'REJECTED'},
    {display: 'COMPLETED', api: 'COMPLETED'},
    {display: 'PAUSED', api: 'PAUSED'},
  ]

// Statuses whose ads are currently running, so date columns are meaningful
export const runningStatuses = ['APPROVED', 'ACTIVATED', 'COMPLETED', 'PAUSED']

// Only a running (APPROVED / ACTIVATED) ad can be paused, and only a PAUSED one can be resumed
export const pausableStatuses = ['APPROVED', 'ACTIVATED', 'PAUSED']

// Pause / resume an ad. The API takes "PAUSED" to pause and "ACTIVATED" to resume;
// once resumed the list reports the ad as APPROVED again.
export const adsPauseResumeApiCall = async (
  vendorId: any,
  adsId: any,
  status: 'ACTIVATED' | 'PAUSED'
) =>
  fetchData('public/adspauseresume', {
    vendorId: String(vendorId),
    adsId: adsId,
    status: status,
  })

// Pause / resume a sponsored (listing) ad. Same contract as adspauseresume,
// but keyed by sponserId: "PAUSED" to pause and "ACTIVATED" to resume.
export const sponserPauseResumeApiCall = async (
  vendorId: any,
  sponserId: any,
  status: 'ACTIVATED' | 'PAUSED'
) =>
  fetchData('public/sponserpauseresume', {
    vendorId: String(vendorId),
    sponserId: sponserId,
    status: status,
  })

export const CLICK_PAYMENT_CONFIG = {
  merchantId: 26181,
  merchantUserId: 41659,
  serviceId: 34207,
}

export const getStatusBadgeClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'DRAFT':
        return 'btn-secondary mx-2 p-1'
      case 'UNDER_REVIEW':
        return 'btn-warning mx-2 p-1'
      case 'APPROVED':
        return 'btn-success mx-2 p-1'
      case 'ACTIVATED':
        return 'btn-primary mx-2 p-1'
      case 'REJECTED':
        return 'btn-danger mx-2 p-1'
      case 'PAUSED':
        return 'btn-info mx-2 p-1'
      case 'COMPLETED':
        return 'btn-success mx-2 p-1'
      default:
        return 'btn-secondary mx-2 p-1'
    }
  }

export const getStatusBadgeColorClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'DRAFT':
        return 'badge-light-secondary'
      case 'UNDER_REVIEW':
        return 'badge-light-warning'
      case 'APPROVED':
        return 'badge-light-success'
      case 'ACTIVATED':
        return 'badge-light-primary'
      case 'REJECTED':
        return 'badge-light-danger'
      case 'PAUSED':
        return 'badge-light-info'
      case 'COMPLETED':
        return 'badge-light-primary'
      default:
        return 'badge-light-secondary'
    }
  }

// User level targeting options (lowercase in form state, capitalized for the API)
export const USER_LEVELS = ['basic', 'silver', 'gold', 'platinum']

export type DurationUnit = 'HOUR' | 'DAY' | 'WEEK' | 'MONTH'

// Duration unit labels per language. Russian needs 3 plural forms
// (1 день, 2-4 дня, 5-20 дней), English 2, Uzbek 1.
const durationUnitLabels: Record<string, Record<DurationUnit, string[]>> = {
  en: {
    HOUR: ['hour', 'hours'],
    DAY: ['day', 'days'],
    WEEK: ['week', 'weeks'],
    MONTH: ['month', 'months'],
  },
  ru: {
    HOUR: ['час', 'часа', 'часов'],
    DAY: ['день', 'дня', 'дней'],
    WEEK: ['неделя', 'недели', 'недель'],
    MONTH: ['месяц', 'месяца', 'месяцев'],
  },
  uz: {
    HOUR: ['soat'],
    DAY: ['kun'],
    WEEK: ['hafta'],
    MONTH: ['oy'],
  },
}

const pluralIndex = (lang: string, count: number) => {
  if (lang === 'uz') return 0
  if (lang === 'ru') {
    const mod10 = count % 10
    const mod100 = count % 100
    if (mod10 === 1 && mod100 !== 11) return 0
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 1
    return 2
  }
  return count === 1 ? 0 : 1
}

// Just the unit word in the form that agrees with `count` ('' when unknown).
// Use it where the number is rendered separately from its unit.
export const durationUnitLabel = (count: number, durationType: any, lang: string) => {
  const type = String(durationType || '').toUpperCase() as DurationUnit
  const labels = (durationUnitLabels[lang] || durationUnitLabels.en)[type]
  return labels ? labels[pluralIndex(lang, count)] : ''
}

// "1 WEEK" -> "1 week" / "1 неделя" / "1 hafta"
export const formatDuration = (duration: any, durationType: any, lang: string) => {
  const count = Number(duration)
  const label = durationUnitLabel(count, durationType, lang)
  if (!Number.isFinite(count) || !label) return '-'
  return `${count} ${label}`
}
